import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import signupdropdown from "../../src/assets/images/icons/signup-dropdown.svg";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const IndividualAccount = ({ handleSubmitForm, formData, setFormData }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const today = new Date();
  const minAllowedDate = new Date();
  // const maxDOB = minAllowedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  const maxDOBDate = new Date();
  maxDOBDate.setFullYear(today.getFullYear() - 13);

  const maxDOB = maxDOBDate.toISOString().split("T")[0];


  const [isValid, setIsValid] = useState(true);
  const [errors, setErrors] = useState({});

  const validateDOB = (dob) => {
    const selectedDate = new Date(dob);
    const todayDate = new Date();
    minAllowedDate.setFullYear(today.getFullYear() - 13);
    // const maxDOB = minAllowedDate.toISOString().split("T")[0];
    setErrors({});
    if (!dob) {
      setErrors((prev) => ({ ...prev, dob: "Date of birth is required." }));
      setIsValid(false);
    } else if (selectedDate > todayDate) {
      setErrors((prev) => ({ ...prev, dob: "Date of birth is required." }));
      setIsValid(false);
    } else if (selectedDate > minAllowedDate) {
      setErrors((prev) => ({
        ...prev,
        dob: "You must be at least 13 years old to register.",
      }));
      setIsValid(false);
    } else {
      setErrors((prev) => ({ ...prev, dob: "" })); // Clear error if valid
      setIsValid(true);
    }
  };

  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    alphanumeric: false,
    specialChar: false,
  });

  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState("");
  const [location, setLocation] = useState("");

  /* ===================== OTP STATE ===================== */
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [timer, setTimer] = useState(300);

  /* ===================== CONSTANTS ===================== */
  const countryOptions = [
    {
      code: "US",
      name: "United States",
      dialCode: "+1",
      flagUrl: "https://flagcdn.com/w40/us.png",
    },
    {
      code: "GB",
      name: "United Kingdom",
      dialCode: "+44",
      flagUrl: "https://flagcdn.com/w40/gb.png",
    },
    {
      code: "CA",
      name: "Canada",
      dialCode: "+1",
      flagUrl: "https://flagcdn.com/w40/ca.png",
    },
    {
      code: "AU",
      name: "Australia",
      dialCode: "+61",
      flagUrl: "https://flagcdn.com/w40/au.png",
    },
    {
      code: "DE",
      name: "Germany",
      dialCode: "+49",
      flagUrl: "https://flagcdn.com/w40/de.png",
    },
    {
      code: "FR",
      name: "France",
      dialCode: "+33",
      flagUrl: "https://flagcdn.com/w40/fr.png",
    },
    {
      code: "JP",
      name: "Japan",
      dialCode: "+81",
      flagUrl: "https://flagcdn.com/w40/jp.png",
    },
    {
      code: "CN",
      name: "China",
      dialCode: "+86",
      flagUrl: "https://flagcdn.com/w40/cn.png",
    },
    {
      code: "IN",
      name: "India",
      dialCode: "+91",
      flagUrl: "https://flagcdn.com/w40/in.png",
    },
    {
      code: "BR",
      name: "Brazil",
      dialCode: "+55",
      flagUrl: "https://flagcdn.com/w40/br.png",
    },
  ];
  const countryOptionsList = useMemo(() => countryList().getData(), []);

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "56px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    if (!showOTPModal) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showOTPModal]);

  /* ===================== HELPERS ===================== */
  const validatePassword = (password) => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      alphanumeric: /[A-Za-z]/.test(password) && /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  // Re-sync the criteria checklist whenever the password value changes for any reason
  // (typing, or being restored from storage after returning from the T&C page)
  useEffect(() => {
    validatePassword(formData.password || "");
  }, [formData.password]);

  const handleCountryChange = (selectedOption) => {
    setFormData((prevState) => ({
      ...prevState,
      countryCode: selectedOption.dialCode, // Update the country code
    }));
  };

  const checkPasswordMatch = (confirmPassword) => {
    setPasswordMatchError(
      confirmPassword !== formData.password ? "Passwords do not match" : ""
    );
  };

  const checkUsernameAvailability = async (username) => {
    if (!username) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/auth/verify-username`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username }),
        }
      );
      const data = await res.json();

      if (data?.result?.available) {
        setUsernameAvailable(true);
        setUsernameMessage("Username is available");
      } else {
        setUsernameAvailable(false);
        setUsernameMessage("Username already taken");
      }
    } catch {
      setUsernameMessage("Error checking username");
    }
  };

  const getLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (!data?.city || !data?.country_name) {
        throw new Error("Location data unavailable");
      }
      const address = `${data.city}, ${data.country_name}`;
      setLocation(address);
      setFormData((p) => ({ ...p, location: address }));
    } catch {
      toast.error(
        "Couldn't detect your location automatically. Please enter it manually below."
      );
    }
  };

  /* ===================== HANDLERS ===================== */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "username") checkUsernameAvailability(value);
    if (name === "password") validatePassword(value);
    if (name === "confirmPassword") checkPasswordMatch(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !passwordCriteria.minLength ||
      !passwordCriteria.alphanumeric ||
      !passwordCriteria.specialChar
    ) {
      toast.error("Password does not meet criteria");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setPasswordMatchError("Passwords do not match");
      return;
    }

    handleSubmitForm(e);
  };

  /* ===================== JSX ===================== */
  return (
    <form onSubmit={handleSubmit} className="space-y-4 signup-scroll">
      <input
        name="firstname"
        maxLength={50}
        placeholder="First Name"
        value={formData.firstname}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      <input
        name="lastname"
        maxLength={50}
        placeholder="Last Name"
        value={formData.lastname}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      <input
        name="username"
        placeholder="Username"
        value={formData.username}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />
      {usernameMessage && (
        <p
          className={`text-sm ${
            usernameAvailable ? "text-green-500" : "text-red-500"
          }`}
        >
          {usernameMessage}
        </p>
      )}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="dob">
          Date of Birth
        </label>
        <input
          id="dob"
          placeholder="DD/MM/YY"
          type="date"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
          onClick={() => validateDOB(formData.dob)}
          max={maxDOB}
          className={`w-full max-w-[400px] h-[60px] px-3 py-2 border ${
            errors.dob ? "border-red-500" : "border-gray-400"
          } rounded-[12px] focus:outline-none focus:ring focus:ring-blue-200`}
        />
        {errors.dob && <p className="text-red-500 text-sm">{errors.dob}</p>}
      </div>
      <div className="space-y-2 relative">
        <div>
          <label className="text-sm font-medium" htmlFor="gender">
            Gender
          </label>
        </div>
        <div className="relative">
          <select
            name="gender"
            className="h-[60px] w-full px-3 pr-12 py-2 border border-gray-400
                        rounded-[12px] appearance-none
                        focus:outline-none focus:ring focus:ring-blue-200"
            onChange={handleChange}
            value={formData.gender}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Others">Prefer not to say</option>
          </select>

          {/* Custom dropdown arrow */}
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
            <img src={signupdropdown} alt="" className="" />
          </span>
        </div>

        {errors.gender && (
          <p className="text-red-500 text-sm">{errors.gender}</p>
        )}
      </div>
      {/* ✅ Email field added */}
      <input
        type="email"
        name="email"
        placeholder="Email"
        value={formData.email}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      <input
        type={showPassword ? "text" : "password"}
        name="password"
        placeholder="Password"
        value={formData.password}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      {/* Password criteria checklist */}
      <div className="password-checkmar text-sm text-gray-600 flex flex-wrap gap-x-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.minLength}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Min 8 characters</span>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.alphanumeric}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Alphanumeric</span>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.specialChar}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Special character</span>
        </div>
      </div>

      <input
        type={showConfirmPassword ? "text" : "password"}
        name="confirmPassword"
        placeholder="Confirm Password"
        value={formData.confirmPassword}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      {passwordMatchError && (
        <p className="text-red-500 text-sm">{passwordMatchError}</p>
      )}

      <input
        name="location"
        value={formData.location || location}
        onChange={handleChange}
        onClick={() => {
          if (!formData.location) getLocation();
        }}
        placeholder="Location (auto-detected, or type your own)"
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      <Select
        options={countryOptionsList}
        styles={customStyles}
        menuPortalTarget={document.body}
        placeholder="Country"
        value={countryOptionsList.find((option) => option.label === formData.country) || null}
        onChange={(opt) =>
          setFormData((p) => ({ ...p, country: opt?.label || "" }))
        }
      />
      {/* Phone Number Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="mobile">
          Mobile Number
        </label>
        <div className="flex space-x-2 items-center">
          <Select
            options={countryOptions}
            getOptionLabel={(e) => (
              <div className="flex items-center">
                <img src={e.flagUrl} alt="" className="w-6 h-4" />
                <span className="ml-2 hidden">{e.dialCode}</span>
              </div>
            )}
            getOptionValue={(e) => e.dialCode}
            value={countryOptions.find(
              (c) => c.dialCode === formData.countryCode
            )}
            onChange={handleCountryChange}
            styles={customStyles}
            menuPortalTarget={document.body} // Ensures popup behavior
            menuPosition="absolute"
          />

          <input
            id="mobile"
            placeholder="Mobile Number"
            type="tel"
            name="mobile"
            value={formData.mobile}
            maxLength={15}
            onChange={handleChange}
            className="w-full max-w-[300px] h-[56px] px-3 py-2 border border-gray-400 rounded-[12px] focus:outline-none focus:ring focus:ring-blue-200"
          />
        </div>
      </div>

      {errors.mobile && <p className="text-red-500 text-sm">{errors.mobile}</p>}

      <button
        type="submit"
        className="w-full h-[56px] bg-[#161F28] text-white rounded-xl hover:bg-[#1B2537]"
      >
        Create Account
      </button>
    </form>
  );
};

export default IndividualAccount;
